import {
  cancellationProtectionPayoutQuerySchema,
  type ListCancellationProtectionPayoutsResponse,
  listCancellationProtectionPayoutsResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { getPageCursor } from "@src/appV2/redesign/Workplace/Review/utils";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { minutesToMilliseconds } from "date-fns";
import { useCallback } from "react";
import { type z } from "zod";

import { CANCELLATION_PROTECTION_PAYOUTS_URL } from "./constants";
import { type ResolvedCancellationProtectionPayout } from "./types";
import { resolveCancellationProtectionPayoutsResponse } from "./utils";

type CancellationProtectionPayoutsQueryInput = z.input<
  typeof cancellationProtectionPayoutQuerySchema
>;

interface UsePaginatedCancellationProtectionPayoutsOptions {
  params: CancellationProtectionPayoutsQueryInput;
  enabled?: boolean;
}

export function useInvalidateCancellationProtectionPayouts() {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [CANCELLATION_PROTECTION_PAYOUTS_URL],
      }),
    [queryClient]
  );
}

export function usePaginatedCancellationProtectionPayouts(
  options: UsePaginatedCancellationProtectionPayoutsOptions
): UseInfiniteQueryResult<ResolvedCancellationProtectionPayout[], AxiosError> {
  const { params, enabled = true } = options;

  return useInfiniteQuery({
    queryKey: [CANCELLATION_PROTECTION_PAYOUTS_URL, params],
    queryFn: async ({ pageParam: cursor }) => {
      const response = await get({
        url: CANCELLATION_PROTECTION_PAYOUTS_URL,
        queryParams: {
          ...params,
          include: params.include ?? ["shift", "workplace"],
          page: {
            ...params.page,
            ...(cursor ? { cursor } : {}),
          },
        },
        requestSchema: cancellationProtectionPayoutQuerySchema,
        responseSchema: listCancellationProtectionPayoutsResponseSchema,
        parseSchema: true,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) =>
      isDefined(lastPage.links.next) ? getPageCursor(lastPage.links.next) : undefined,
    enabled,
    staleTime: minutesToMilliseconds(10),
    select: ({ pages, pageParams }) => {
      return {
        pages: pages.map((page) =>
          resolveCancellationProtectionPayoutsResponse(
            page as ListCancellationProtectionPayoutsResponse
          )
        ),
        pageParams,
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_CANCELLATION_PROTECTION_PAYOUT_FAILURE,
    },
  });
}
